import React from 'react'
import styled from 'styled-components'
import { Switch as BaseSwitch } from '@base-ui-components/react/switch'
import type { Theme } from '../../theme/types'

export interface SwitchProps
    extends React.ComponentProps<typeof BaseSwitch.Root> {}

const isDarkTheme = (theme: Theme): boolean =>
    theme.colors.background.bg1 === '#2a2a2a'

const StyledRoot = styled(BaseSwitch.Root)`
    position: relative;
    display: flex;
    appearance: none;
    border: 0;
    margin: 0;
    padding: 1px;
    width: 2.5rem;
    height: 1.5rem;
    border-radius: 1.5rem;
    outline: 1px solid;
    outline-offset: -1px;
    background-color: transparent;
    background-image: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.text.secondary} 35%,
        ${({ theme }) => theme.colors.border.primary} 65%
    );
    background-size: 6.5rem 100%;
    background-position-x: 100%;
    background-repeat: no-repeat;
    transition-property: background-position, box-shadow;
    transition-timing-function: cubic-bezier(0.26, 0.75, 0.38, 0.45);
    transition-duration: 125ms;

    &:active {
        background-color: ${({ theme }) => theme.colors.greyScale[600]};
        background-color: ${({ theme }) => theme.colors.info};
    }

    &[data-checked] {
        background-position-x: 0%;
    }

    &[data-checked]:active {
        background-color: ${({ theme }) => theme.colors.info};
    }

    box-shadow: 0 1.5px 2px inset ${({ theme }) => theme.colors.border.primary};
    outline-color: ${({ theme }) => theme.colors.border.primary};

    &[data-checked] {
        box-shadow: ${({ theme }) =>
            isDarkTheme(theme)
                ? 'none'
                : `0 1.5px 2px inset ${theme.colors.border.primary}`};
        background-color: ${({ theme }) => theme.colors.info};
    }

    &:focus-visible {
        &::before {
            content: '';
            inset: 0;
            position: absolute;
            border-radius: inherit;
            outline: 2px solid ${({ theme }) => theme.colors.info};
            outline-offset: 2px;
        }
    }
    background-color: ${({ theme }) => theme.colors.info};
`

const StyledThumb = styled(BaseSwitch.Thumb)`
    aspect-ratio: 1 / 1;
    height: 100%;
    border-radius: 100%;
    background-color: #ffffff;
    transition: translate 150ms ease;

    &[data-checked] {
        translate: 1rem 0;
    }

    box-shadow: ${({ theme }: { theme: Theme }) =>
        isDarkTheme(theme)
            ? `0 0 1px 1px rgb(0 0 0 / 25%),
       0 1px 1px rgb(0 0 0 / 25%),
       1px 2px 4px -1px rgb(0 0 0 / 25%)`
            : `0 0 1px 1px rgb(0 0 0 / 10%),
       0 1px 1px rgb(0 0 0 / 10%),
       1px 2px 4px -1px rgb(0 0 0 / 10%)`};
`

export const Switch: React.FC<SwitchProps> = (props) => {
    return (
        <StyledRoot
            onClick={(e) => {
                if (!props.onCheckedChange) {
                    e.preventDefault()
                }
            }}
            {...props}
        >
            <StyledThumb />
        </StyledRoot>
    )
}
